/**
 * AnalyticsPanel — the workspace's right column (and the Classic view's lower right).
 * Summary (max P/L, breakevens, POP, EV, net premium, R:R, net greeks) over four tabs —
 * Payoff · P&L table · Heatmap · Greeks — driven by three what-if sliders:
 * spot, date (now → horizon) and an IV shift in vol points.
 */
import React, { useMemo } from 'react';
import { Box, Slider, IconButton, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { WsLeg, WsStats, positionPnl, netGreeks, moveOdds } from './wsMath';
import type { DailyBar } from './wsData';
import { WsColors, Ccy, fmtMoney, fmtNum, fmtStrike, WsPrefs, expLabel } from './wsUi';
import PayoffGraph from './PayoffGraph';

export interface WhatIf { spotPct: number; timeFrac: number; ivShift: number }

/** Close / roll preview: the position as it is NOW (legs marked CLOSE kept, replacements left out). */
export interface WsCompare { legs: WsLeg[]; stats: WsStats; closing: number; opening: number }

interface Props {
  legs: WsLeg[];
  stats: WsStats;
  compare?: WsCompare | null;
  spot: number;
  cv: number;
  atmIv: number;
  base: string;
  /** ~3y of daily candles for the historical move odds (null while loading). */
  bars?: DailyBar[] | null;
  /** Open interest by strike for the expiry the chain is showing. */
  oi?: { iso: string; rows: Array<{ strike: number; ceOi: number; peOi: number }> } | null;
  prefs: WsPrefs;
  setPrefs: (p: Partial<WsPrefs>) => void;
  whatIf: WhatIf;
  setWhatIf: (w: WhatIf) => void;
  c: WsColors;
}

const TABS: Array<[WsPrefs['tab'], string]> = [['payoff', 'Payoff graph'], ['table', 'P&L table'], ['heatmap', 'Heatmap'], ['greeks', 'Greeks']];

const AnalyticsPanel: React.FC<Props> = ({ legs, stats, compare, spot, cv, atmIv, base, bars, oi, prefs, setPrefs, whatIf, setWhatIf, c }) => {
  const ccy: Ccy = prefs.ccy;
  const now = Date.now();
  const H = stats.horizon;
  const hasLegs = legs.some(l => l.enabled) && H != null && spot > 0;
  const atMs = H != null ? now + whatIf.timeFrac * Math.max(0, H - now) : now;
  const wSpot = spot * (1 + whatIf.spotPct / 100);
  const projected = hasLegs ? positionPnl(legs, wSpot, atMs, cv, whatIf.ivShift) : 0;
  const greeks = useMemo(() => (hasLegs ? netGreeks(legs, wSpot, atMs, cv, whatIf.ivShift) : null), [hasLegs, legs, wSpot, atMs, cv, whatIf.ivShift]);

  const money = (v: number | null | undefined, sign = false) => fmtMoney(v, ccy, { sign });
  const pnlColor = (v: number) => (v > 0 ? c.green : v < 0 ? c.red : c.text);

  const stat = (label: string, value: React.ReactNode, color?: string, tip?: string, was?: React.ReactNode) => (
    <Tooltip title={tip || ''} disableInteractive enterDelay={500}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: 9.5, letterSpacing: 0.6, color: c.faint, fontWeight: 700 }}>{label}</Box>
        <Box sx={{ fontSize: 15, fontWeight: 700, color: color || c.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Box>
        {was != null && <Box data-testid="ws-stat-was" sx={{ fontSize: 10, color: c.faint, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>now {was}</Box>}
      </Box>
    </Tooltip>
  );
  const cs = compare?.stats;
  const cmpOn = !!compare && cs?.horizon != null;

  const timeLabel = (ms: number) => {
    if (ms - now < 60e3) return 'Now';
    const d = new Date(ms);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // ── P&L table & heatmap price ladder: centred on spot, ~σ/3 per row at the horizon ──
  const ladder = useMemo(() => {
    if (!hasLegs) return [] as number[];
    const tH = Math.max(0, (H as number) - now) / (365 * 86400e3);
    const sd = Math.max(0.004, (atmIv || 0.5) * Math.sqrt(Math.max(tH, 1 / 365)));
    const stepPct = Math.max(0.0025, sd / 3);
    const raw = Array.from({ length: 15 }, (_, i) => spot * (1 + (7 - i) * stepPct));
    // snap to a readable increment
    const inc = spot > 20000 ? 50 : spot > 2000 ? 5 : 1;
    return raw.map(x => Math.round(x / inc) * inc);
  }, [hasLegs, H, now, atmIv, spot]);

  const heatCols = useMemo(() => {
    if (!hasLegs) return [] as number[];
    const span = Math.max(0, (H as number) - now);
    return [0, 1, 2, 3, 4, 5].map(i => now + (span * i) / 6).concat([H as number]);
  }, [hasLegs, H, now]);

  const maxLossAbs = Number.isFinite(stats.maxLoss) && stats.maxLoss < 0 ? Math.abs(stats.maxLoss) : null;

  const slider = (label: string, value: number, display: React.ReactNode, sub: React.ReactNode, min: number, max: number, step: number, set: (v: number) => void) => (
    <Box sx={{ flex: 1, minWidth: 140, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ fontSize: 9.5, letterSpacing: 0.6, color: c.faint, fontWeight: 700 }}>{label}</Box>
        <Box sx={{ fontSize: 12, fontWeight: 700, color: c.text, whiteSpace: 'nowrap' }}>{display} <Box component="span" sx={{ color: c.faint, fontWeight: 400, fontSize: 10 }}>{sub}</Box></Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" sx={{ p: 0.2 }} onClick={() => set(Math.max(min, +(value - step).toFixed(4)))}><ChevronLeftIcon sx={{ fontSize: 16 }} /></IconButton>
        <Slider size="small" value={value} min={min} max={max} step={step} onChange={(_e, v) => set(v as number)}
          sx={{ color: c.amber, py: 0.8, '& .MuiSlider-thumb': { width: 12, height: 12 } }} />
        <IconButton size="small" sx={{ p: 0.2 }} onClick={() => set(Math.min(max, +(value + step).toFixed(4)))}><ChevronRightIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
    </Box>
  );

  const g = greeks?.total;

  // ── breakeven distances (%, σ), range width, and how often history went past them ──
  const beInfo = useMemo(() => {
    if (!hasLegs || !stats.breakevens.length) return null;
    const tH = Math.max(0, (H as number) - now) / (365 * 86400e3);
    const sd = (atmIv || 0.5) * Math.sqrt(Math.max(tH, 1 / 365));
    const rows = stats.breakevens.map(b => ({ b, pct: b / spot - 1, sig: Math.log(b / spot) / sd }));
    const below = rows.filter(r => r.b < spot).pop() || null;
    const above = rows.find(r => r.b > spot) || null;
    const width = below && above ? above.b - below.b : null;
    const days = Math.max(1, Math.ceil(((H as number) - now) / 86400e3));
    const odds = bars && bars.length ? moveOdds(bars, days, below ? below.pct : null, above ? above.pct : null) : null;
    return { rows, below, above, width, days, odds };
  }, [hasLegs, stats.breakevens, H, now, atmIv, spot, bars]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* tabs + currency */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.6, borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.3 }}>
          {TABS.map(([k, label]) => (
            <Box key={k} component="button" type="button" onClick={() => setPrefs({ tab: k })}
              sx={{ all: 'unset', cursor: 'pointer', fontSize: 12, px: 1, py: 0.35, borderRadius: 0.8, fontWeight: prefs.tab === k ? 700 : 500,
                color: prefs.tab === k ? c.text : c.dim, bgcolor: prefs.tab === k ? c.panel : 'transparent', boxShadow: prefs.tab === k ? 1 : 0 }}>
              {label}
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', border: `1px solid ${c.border}`, borderRadius: 1, overflow: 'hidden' }}>
          {(['INR', 'USD'] as Ccy[]).map(k => (
            <Box key={k} component="button" type="button" onClick={() => setPrefs({ ccy: k })}
              sx={{ all: 'unset', cursor: 'pointer', px: 0.8, py: 0.2, fontSize: 11, fontWeight: 700, color: prefs.ccy === k ? c.amber : c.dim, bgcolor: prefs.ccy === k ? c.amberBg : 'transparent' }}>
              {k === 'INR' ? '₹' : '$'}
            </Box>
          ))}
        </Box>
      </Box>

      {/* summary */}
      <Box sx={{ px: 1.2, pt: 1, pb: 0.8, borderBottom: `1px solid ${c.border}` }}>
        {compare && (
          <Box data-testid="ws-close-preview" sx={{ mb: 0.8, px: 0.9, py: 0.45, borderRadius: 1, borderLeft: `3px solid ${c.amber}`, bgcolor: c.amberBg, fontSize: 11.5, lineHeight: 1.4 }}>
            <b>Preview after {compare.closing ? `closing ${compare.closing} leg${compare.closing > 1 ? 's' : ''}` : ''}{compare.closing && compare.opening ? ' and ' : ''}{compare.opening ? `opening ${compare.opening}` : ''}</b>
            <span style={{ color: c.dim }}> — the figures and solid curves show the position after the change (closed legs booked at what closing gets now); the small “now” figures and the dashed grey curve are the position as it is. Nothing is sent yet.</span>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', rowGap: 0.8, columnGap: 1.5 }}>
          {stat('MAX PROFIT', hasLegs ? money(stats.maxProfit) : '—', hasLegs ? c.green : undefined, 'Best P&L at the horizon (earliest leg expiry). Unlimited when net long calls.', cmpOn ? money(cs!.maxProfit) : undefined)}
          {stat('MAX LOSS', hasLegs ? money(stats.maxLoss) : '—', hasLegs ? c.red : undefined, 'Worst P&L at the horizon. Unlimited when net short calls.', cmpOn ? money(cs!.maxLoss) : undefined)}
          {stat('POP', hasLegs ? `${(stats.pop * 100).toFixed(0)}%` : '—', hasLegs ? (stats.pop >= 0.5 ? c.green : c.amber) : undefined, 'Probability of profit at the horizon — lognormal on ATM IV.', cmpOn ? `${(cs!.pop * 100).toFixed(0)}%` : undefined)}
          {stat('BREAKEVEN' + (stats.breakevens.length > 1 ? 'S' : ''), hasLegs && stats.breakevens.length ? stats.breakevens.map(b => fmtStrike(Math.round(b))).join(' / ') : '—', undefined, undefined,
            cmpOn ? (cs!.breakevens.length ? cs!.breakevens.map(b => fmtStrike(Math.round(b))).join(' / ') : 'none') : undefined)}
          {stat('EXPECTED VALUE', hasLegs ? money(stats.ev, true) : '—', hasLegs ? pnlColor(stats.ev) : undefined, 'Probability-weighted horizon P&L under the same lognormal. Near zero means the legs are priced fairly vs ATM IV.', cmpOn ? money(cs!.ev, true) : undefined)}
          {stat(stats.net >= 0 ? 'NET CREDIT' : 'NET DEBIT', hasLegs ? money(Math.abs(stats.net)) : '—', hasLegs ? (stats.net >= 0 ? c.green : c.red) : undefined)}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.6, rowGap: 0.3, mt: 0.9, fontSize: 11, color: c.dim, fontVariantNumeric: 'tabular-nums' }}>
          <span>R:R <b style={{ color: c.text }}>{stats.rr != null ? `1 : ${stats.rr.toFixed(2)}` : '—'}</b></span>
          <Tooltip title={`Delta exposure: net delta ${fmtNum(g?.delta, 4)} ${base} × index — the P&L of a 1-point index move is that ÷ index`}><span>Δ <b style={{ color: g ? pnlColor(g.delta) : c.text }}>{g ? money(g.delta * wSpot, true) : '—'}</b></span></Tooltip>
          <span>Γ <b style={{ color: c.text }}>{fmtNum(g?.gamma, 6)}</b></span>
          <Tooltip title="Theta per day"><span>Θ/day <b style={{ color: g ? pnlColor(g.theta) : c.text }}>{g ? money(g.theta, true) : '—'}</b></span></Tooltip>
          <Tooltip title="Vega per 1 vol point"><span>Vega <b style={{ color: c.text }}>{g ? money(g.vega, true) : '—'}</b></span></Tooltip>
          <span>σ ATM <b style={{ color: c.amber }}>{atmIv ? `${(atmIv * 100).toFixed(1)}%` : '—'}</b></span>
        </Box>
        {beInfo && (
          <Box data-testid="ws-be-info" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.6, rowGap: 0.3, mt: 0.5, fontSize: 11, color: c.dim, fontVariantNumeric: 'tabular-nums' }}>
            <Tooltip title="Breakeven distance from the index, in % and in standard deviations of the move expected by the horizon (ATM IV).">
              <span>BE {beInfo.rows.map((r, i) => (
                <b key={i} style={{ color: c.text }}>{i ? ' / ' : ''}{r.pct >= 0 ? '+' : ''}{(r.pct * 100).toFixed(1)}% <span style={{ color: c.faint, fontWeight: 400 }}>({r.sig >= 0 ? '+' : ''}{r.sig.toFixed(1)}σ)</span></b>
              ))}</span>
            </Tooltip>
            {beInfo.width != null && <span>Range <b style={{ color: c.text }}>{fmtStrike(Math.round(beInfo.width))}</b> <span style={{ color: c.faint }}>({((beInfo.width / spot) * 100).toFixed(1)}%)</span></span>}
            <Tooltip title={beInfo.odds
              ? `Over the last ${beInfo.odds.windows} overlapping ${beInfo.days}-day windows of ${base} daily candles: the share that ENDED beyond the breakevens, and the share that TRADED beyond them at some point (daily highs/lows). History, not a forecast.`
              : 'Loading daily history…'}>
              <span>Past {beInfo.days}d windows beyond BE: {beInfo.odds
                ? <><b style={{ color: beInfo.odds.endOut > 0.2 ? c.red : c.text }}>{(beInfo.odds.endOut * 100).toFixed(0)}%</b> at end · <b style={{ color: c.text }}>{(beInfo.odds.touchOut * 100).toFixed(0)}%</b> touched</>
                : <span style={{ color: c.faint }}>—</span>}</span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!hasLegs ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: c.dim, gap: 0.5, p: 2, textAlign: 'center' }}>
            <Box sx={{ fontWeight: 700, color: c.text }}>No payoff yet</Box>
            <Box sx={{ fontSize: 12 }}>Add a leg from the chain, or pick a ready-made strategy, to draw it here.</Box>
          </Box>
        ) : prefs.tab === 'payoff' ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 1.2, pt: 0.7, fontSize: 11, color: c.dim, flexWrap: 'wrap' }}>
              <span><Box component="span" sx={{ display: 'inline-block', width: 14, height: 2, bgcolor: c.red, verticalAlign: 'middle', mr: 0.5 }} />At expiry</span>
              <span><Box component="span" sx={{ display: 'inline-block', width: 14, height: 2, bgcolor: c.blue, verticalAlign: 'middle', mr: 0.5 }} />{whatIf.timeFrac === 0 ? 'Today (T+0)' : `At ${timeLabel(atMs)}`}</span>
              {cmpOn && <span><Box component="span" sx={{ display: 'inline-block', width: 14, height: 0, borderTop: `2px dashed ${c.dim}`, verticalAlign: 'middle', mr: 0.5 }} />Now, at expiry</span>}
              <Box component="button" type="button" onClick={() => setPrefs({ showProb: !prefs.showProb })}
                sx={{ all: 'unset', cursor: 'pointer', px: 0.8, py: 0.1, borderRadius: 1, border: `1px solid ${prefs.showProb ? c.amber : c.border}`, color: prefs.showProb ? c.amber : c.dim }}>
                Probability
              </Box>
              {(() => {
                const legExp = legs.find(l => l.enabled && !l.closing && l.settleMs === H)?.expiry;
                const other = oi && legExp && oi.iso !== legExp;
                return (
                  <Tooltip title={oi ? `Open interest by strike for ${expLabel(oi.iso)}${other ? ` — the chain's expiry, not your legs' (${expLabel(legExp!)}); open that expiry to see its OI` : ''}. Calls left of each strike, puts right; the three biggest per side are the walls.` : 'No chain loaded'}>
                    <Box component="button" type="button" onClick={() => setPrefs({ showOi: !prefs.showOi })}
                      sx={{ all: 'unset', cursor: 'pointer', px: 0.8, py: 0.1, borderRadius: 1, border: `1px solid ${prefs.showOi ? c.blue : c.border}`, color: prefs.showOi ? c.blue : c.dim, whiteSpace: 'nowrap' }}>
                      OI walls{prefs.showOi && oi ? ` · ${expLabel(oi.iso)}` : ''}{prefs.showOi && other ? ' ⚠' : ''}
                    </Box>
                  </Tooltip>
                );
              })()}
              <Box sx={{ flex: 1 }} />
              <Box sx={{ px: 0.9, py: 0.2, borderRadius: 1, fontWeight: 700, bgcolor: projected >= 0 ? c.greenBg : c.redBg, color: pnlColor(projected) }}>
                Projected P&L: {money(projected, true)}
              </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, px: 0.5 }}>
              <PayoffGraph legs={legs} spot={spot} whatIfSpot={wSpot} atMs={atMs} horizon={H as number} ivShift={whatIf.ivShift}
                cv={cv} atmIv={atmIv} breakevens={stats.breakevens} ccy={ccy} showProb={prefs.showProb}
                oiRows={oi?.rows} showOi={prefs.showOi} c={c}
                compareLegs={cmpOn ? compare!.legs : undefined} compareHorizon={cmpOn ? cs!.horizon as number : undefined} />
            </Box>
          </>
        ) : prefs.tab === 'table' ? (
          <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.6 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums',
              '& th': { color: c.faint, fontWeight: 700, fontSize: 10, textAlign: 'right', py: 0.6, px: 1, borderBottom: `1px solid ${c.border}`, position: 'sticky', top: 0, bgcolor: c.panel },
              '& td': { textAlign: 'right', py: 0.5, px: 1, borderBottom: `1px solid ${c.border}` } }}>
              <thead><tr><th style={{ textAlign: 'left' }}>{base} at</th><th>Move</th><th>{whatIf.timeFrac === 0 ? 'Today' : timeLabel(atMs)}</th><th>At expiry</th></tr></thead>
              <tbody>
                {ladder.map(px => {
                  const t0 = positionPnl(legs, px, atMs, cv, whatIf.ivShift), te = positionPnl(legs, px, H as number, cv);
                  const isSpot = Math.abs(px - spot) <= (ladder[0] - ladder[1]) / 2;
                  return (
                    <tr key={px} style={{ background: isSpot ? c.amberBg : undefined }}>
                      <td style={{ textAlign: 'left', fontWeight: 700 }}>{fmtStrike(px)}</td>
                      <td style={{ color: c.dim }}>{((px / spot - 1) * 100).toFixed(2)}%</td>
                      <td style={{ color: pnlColor(t0), fontWeight: 600 }}>{money(t0, true)}</td>
                      <td style={{ color: pnlColor(te), fontWeight: 600 }}>{money(te, true)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        ) : prefs.tab === 'heatmap' ? (
          <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.6 }}>
              <Box sx={{ fontSize: 11, color: c.dim }}>{base} price × date</Box>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', border: `1px solid ${c.border}`, borderRadius: 1, overflow: 'hidden' }}>
                {([['money', ccy === 'INR' ? '₹ P&L' : '$ P&L'], ['pct', '% of max loss']] as const).map(([k, l]) => (
                  <Box key={k} component="button" type="button" disabled={k === 'pct' && maxLossAbs == null} onClick={() => setPrefs({ heatUnit: k })}
                    sx={{ all: 'unset', cursor: 'pointer', px: 0.8, py: 0.2, fontSize: 11, color: prefs.heatUnit === k ? c.text : c.dim, bgcolor: prefs.heatUnit === k ? c.hover : 'transparent', opacity: k === 'pct' && maxLossAbs == null ? 0.4 : 1 }}>{l}</Box>
                ))}
              </Box>
            </Box>
            {(() => {
              const cells = ladder.map(px => heatCols.map(t => positionPnl(legs, px, t, cv, t === H ? 0 : whatIf.ivShift)));
              const absMax = Math.max(1e-9, ...cells.flat().map(Math.abs));
              const pct = prefs.heatUnit === 'pct' && maxLossAbs != null;
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: `76px repeat(${heatCols.length}, minmax(48px,1fr))`, gap: '2px', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                  <Box sx={{ color: c.faint, fontSize: 10, fontWeight: 700, alignSelf: 'end', pb: 0.4 }}>{base} at</Box>
                  {heatCols.map((t, j) => (
                    <Box key={j} sx={{ color: c.faint, fontSize: 10, fontWeight: 700, textAlign: 'center', pb: 0.4 }}>
                      {j === 0 ? 'Now' : j === heatCols.length - 1 ? 'Expiry' : new Date(t).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, ...(t - now > 86400e3 ? { day: '2-digit', month: 'short' } : {}) })}
                    </Box>
                  ))}
                  {ladder.map((px, i) => {
                    const isSpot = Math.abs(px - spot) <= (ladder[0] - ladder[1]) / 2;
                    return (
                      <React.Fragment key={px}>
                        <Box sx={{ textAlign: 'right', pr: 0.8, fontWeight: 700, color: isSpot ? c.amber : c.text, lineHeight: 1.1, alignSelf: 'center' }}>
                          {fmtStrike(px)}<Box sx={{ fontSize: 9, color: c.faint, fontWeight: 400 }}>{((px / spot - 1) * 100).toFixed(1)}%</Box>
                        </Box>
                        {cells[i].map((v, j) => {
                          const a = Math.min(1, Math.abs(v) / absMax);
                          const bg = v >= 0 ? `rgba(38,194,129,${0.08 + a * 0.5})` : `rgba(240,97,109,${0.08 + a * 0.5})`;
                          return (
                            <Box key={j} sx={{ bgcolor: bg, borderRadius: 0.5, textAlign: 'center', py: 0.8, fontWeight: 600, color: c.text,
                              outline: isSpot ? `1px solid ${c.amber}` : 'none' }}>
                              {pct ? `${((v / (maxLossAbs as number)) * 100).toFixed(0)}%` : fmtMoney(v, ccy, { sign: true })}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </Box>
              );
            })()}
            <Box sx={{ mt: 0.8, fontSize: 10, color: c.faint }}>Model values: Black-Scholes on each leg's IV{whatIf.ivShift ? ` (${whatIf.ivShift > 0 ? '+' : ''}${whatIf.ivShift} vol pts)` : ''}, same as the payoff graph's T+x line.</Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.6 }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums',
              '& th': { color: c.faint, fontWeight: 700, fontSize: 10, textAlign: 'right', py: 0.6, px: 0.8, borderBottom: `1px solid ${c.border}` },
              '& td': { textAlign: 'right', py: 0.6, px: 0.8, borderBottom: `1px solid ${c.border}` } }}>
              <thead><tr><th style={{ textAlign: 'left' }}>Leg</th><th>Qty ({base})</th><th>Delta</th><th>Gamma</th><th>Theta/day</th><th>Vega</th><th>IV</th></tr></thead>
              <tbody>
                {greeks?.rows.map(r => (
                  <tr key={r.leg.id}>
                    <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <b style={{ color: r.leg.action === 'BUY' ? c.green : c.red }}>{r.leg.action[0]}</b> {fmtStrike(r.leg.strike)} {r.leg.optionType}
                      <span style={{ color: c.faint, fontSize: 10 }}> {expLabel(r.leg.expiry)}</span>
                    </td>
                    <td>{fmtNum(r.qty, 4)}</td>
                    <td>{fmtNum(r.delta, 4)}</td>
                    <td>{fmtNum(r.gamma, 6)}</td>
                    <td style={{ color: pnlColor(r.theta) }}>{money(r.theta, true)}</td>
                    <td>{money(r.vega, true)}</td>
                    <td>{(r.iv * 100).toFixed(1)}%</td>
                  </tr>
                ))}
                {greeks && (
                  <tr style={{ fontWeight: 700 }}>
                    <td style={{ textAlign: 'left' }}>Net</td><td />
                    <td>{fmtNum(greeks.total.delta, 4)}</td>
                    <td>{fmtNum(greeks.total.gamma, 6)}</td>
                    <td style={{ color: pnlColor(greeks.total.theta) }}>{money(greeks.total.theta, true)}</td>
                    <td>{money(greeks.total.vega, true)}</td><td />
                  </tr>
                )}
              </tbody>
            </Box>
            <Box sx={{ mt: 0.8, fontSize: 10, color: c.faint }}>At {fmtStrike(Math.round(wSpot))}, {timeLabel(atMs)}. Delta in {base}; theta per day; vega per 1 vol point.</Box>
          </Box>
        )}
      </Box>

      {/* what-if sliders */}
      <Box sx={{ display: 'flex', alignItems: 'center', borderTop: `1px solid ${c.border}`, py: 0.5, px: 0.5, flexWrap: 'wrap', rowGap: 0.3 }}>
        {slider(`${base} AT`, whatIf.spotPct, fmtStrike(Math.round(wSpot)), `${whatIf.spotPct >= 0 ? '+' : ''}${whatIf.spotPct.toFixed(1)}%`, -15, 15, 0.5, v => setWhatIf({ ...whatIf, spotPct: v }))}
        {slider('DATE', whatIf.timeFrac, timeLabel(atMs), H ? `${Math.max(0, (H - atMs) / 3600e3).toFixed(0)}h left` : '', 0, 1, 0.02, v => setWhatIf({ ...whatIf, timeFrac: v }))}
        {slider('IV SHIFT', whatIf.ivShift, `${whatIf.ivShift > 0 ? '+' : ''}${whatIf.ivShift} pts`, `σ ${((atmIv + whatIf.ivShift / 100) * 100).toFixed(1)}%`, -20, 40, 1, v => setWhatIf({ ...whatIf, ivShift: v }))}
        <Tooltip title="Reset what-if"><IconButton size="small" onClick={() => setWhatIf({ spotPct: 0, timeFrac: 0, ivShift: 0 })}><RestartAltIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Box>
    </Box>
  );
};

export default AnalyticsPanel;
